import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { InfoIcon as Info, ShieldCheckIcon as ShieldCheck, GlobeIcon as Globe, ScalesIcon as Scales } from "@phosphor-icons/react";

/**
 * Quiet, always-reachable Buyer Notice surface for the marketplace.
 *
 * Two exports:
 *   1. <BuyerNoticePill /> — small chip that lives in the page header
 *      next to the marketplace title. Click → opens the side-sheet.
 *      Pulses gold once on first visit (per browser) to draw the eye,
 *      then stays quiet.
 *   2. useBuyerNotice() — tiny hook that exposes the same open/close
 *      handlers so other surfaces (the inline consent gate below
 *      Buy/Inquire/Offer, restricted-breed warnings, etc.) can deep-link
 *      into the same sheet without a second instance.
 *
 * Persistence keys (localStorage):
 *   `buyer_notice_seen` — "1" once the pill has been pulsed/read once;
 *                          suppresses the pulse animation on later
 *                          marketplace visits.
 *   `buyer_general_ack` — "1" once the user has explicitly
 *                          acknowledged the general buyer checklist;
 *                          unrestricted-breed inquiries no longer show
 *                          the inline consent gate after this is set.
 */

// Shared module-level state to keep both pill + gate consumers in sync
// without prop-drilling. We expose a tiny pub/sub for the "general ack"
// flag so that ticking the box on one card propagates to all others
// rendered on the same page (e.g., a 24-card marketplace grid).
const listeners = new Set();
function notify() {
  listeners.forEach((fn) => fn(getGeneralAck()));
}

export function getGeneralAck() {
  try { return localStorage.getItem("buyer_general_ack") === "1"; } catch { return false; }
}
export function setGeneralAck(v) {
  try {
    if (v) localStorage.setItem("buyer_general_ack", "1");
    else localStorage.removeItem("buyer_general_ack");
  } catch { /* private mode */ }
  notify();
}
export function useGeneralAck() {
  const [ack, setAck] = useState(getGeneralAck());
  useEffect(() => {
    listeners.add(setAck);
    return () => listeners.delete(setAck);
  }, []);
  return [ack, setGeneralAck];
}

export function BuyerNoticePill({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-buyer-notice", handler);
    return () => window.removeEventListener("open-buyer-notice", handler);
  }, []);

  useEffect(() => {
    let seen = false;
    try { seen = localStorage.getItem("buyer_notice_seen") === "1"; } catch {}
    if (!seen) {
      setPulse(true);
      const t = setTimeout(() => {
        setPulse(false);
        try { localStorage.setItem("buyer_notice_seen", "1"); } catch {}
      }, 6000);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="buyer-notice-pill"
          aria-label="Buyer notice — verify local laws apply"
          onClick={() => {
            setPulse(false);
            try { localStorage.setItem("buyer_notice_seen", "1"); } catch {}
          }}
          className={`relative inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 h-8 text-[12px] text-[#3F3F3F] hover:border-[#C9A227] hover:text-[#0E0E0E] transition-colors ${className}`}
        >
          {pulse && (
            <span
              className="absolute inset-0 rounded-full ring-2 ring-[#C9A227] animate-ping pointer-events-none"
              style={{ animationDuration: "1.6s", animationIterationCount: 3 }}
            />
          )}
          <Info size={13} weight="duotone" className="text-gold" />
          Buyer notice
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="buyer-notice-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-spectral text-2xl tracking-tight">
            Quick buyer checklist
          </SheetTitle>
          <SheetDescription>
            Five things to verify before you buy a dog through any
            marketplace — including this one.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6 text-[13px] text-[#3F3F3F] leading-relaxed">
          <Item icon={<Scales size={18} weight="duotone" className="text-gold" />} title="Local livestock laws">
            Dog-sale regulations vary by US state and country. Verify
            waiting periods, mandatory health certifications, micro-chip
            and vaccination disclosure rules in your jurisdiction.
          </Item>
          <Item icon={<Globe size={18} weight="duotone" className="text-gold" />} title="Restricted breeds">
            Pit Bull–type, Tosa, Dogo Argentino, Fila Brasileiro, and
            wolf-hybrid ownership is regulated or banned in parts of the
            UK, Germany, Australia, and several US municipalities. The
            registry surfaces a clear warning on those listings — check
            your area before purchasing.
          </Item>
          <Item icon={<ShieldCheck size={18} weight="duotone" className="text-gold" />} title="USDA APHIS license">
            Buyers should verify USDA APHIS license status for any
            breeder selling more than four litters per calendar year. The
            registry does not certify USDA compliance.
          </Item>
          <Item icon={<Info size={18} weight="duotone" className="text-gold" />} title="Health guarantees">
            Some listings publish a structured health guarantee. Read it
            carefully; the registry's Escrow & Dispute Resolution
            framework recognizes those guarantees during a dispute.
          </Item>
          <Item icon={<Info size={18} weight="duotone" className="text-gold" />} title="Payments through escrow">
            Use the platform's <strong>Buy Now</strong> or accepted-offer
            flow whenever possible. Funds are held until pickup is
            confirmed and you have a 14-day dispute window. Off-platform
            cash deals waive every protection on this list.
          </Item>
          <Item icon={<Scales size={18} weight="duotone" className="text-gold" />} title="Sales tax is owed to (and by) the seller">
            The Registry is a venue, not the seller. Any sales tax,
            registration fee, or local levy owed on a dog purchase is
            handled between you and the breeder directly. We do not
            collect or remit sales tax on the seller's behalf — if a
            breeder collects it from you, recover it from them, not us.
          </Item>
        </div>

        <div className="mt-7 rounded-xl bg-[#FBF7EC] border border-[#E8D9A6] p-4">
          <p className="text-[12px] text-[#8A6A00] leading-relaxed">
            Armstrong K9 Registry is a venue, not the seller of any
            listed dog. See our{" "}
            <a href="/terms" className="underline">Terms</a>,{" "}
            <a href="/refund" className="underline">Refund Policy</a>, and{" "}
            <a href="/privacy" className="underline">Privacy Policy</a> for the
            complete agreement.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Item({ icon, title, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-8 w-8 rounded-lg bg-[#FBF7EC] flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-ink">{title}</p>
        <p className="mt-0.5">{children}</p>
      </div>
    </div>
  );
}

export default BuyerNoticePill;
